import { useEffect, useState } from "react";
import { ArrowDownCircle, ArrowLeftRight, ArrowUpCircle, Landmark, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { downloadExcel, fmtErr } from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney, todayISO } from "../utils/format";

export default function Treasury() {
  const [accounts, setAccounts] = useState([]);
  const [txns, setTxns] = useState([]);
  const [meta, setMeta] = useState(null);
  const [openTxn, setOpenTxn] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);
  const [filterAccount, setFilterAccount] = useState("");
  const [txnForm, setTxnForm] = useState({ direction: "in", description: "", amount: "", account_id: "", category: "Autre", reference: "", date: todayISO() });
  const [openTransfer, setOpenTransfer] = useState(false);
  const [trForm, setTrForm] = useState({ from_account_id: "", to_account_id: "", amount: "" });
  const [accForm, setAccForm] = useState({ name: "", type: "Caisse", balance: "" });
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const delTxn = async (t) => {
    if (!window.confirm(`Supprimer le mouvement ${t.txn_number || ""} (${t.description}) ? Le solde du compte sera ajusté.`)) return;
    try {
      await api.delete(`/transactions/${t.id}`);
      toast.success("Mouvement supprimé");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const delAccount = async (a) => {
    if (!window.confirm(`Supprimer le compte « ${a.name} » ?`)) return;
    try {
      await api.delete(`/accounts/${a.id}`);
      toast.success("Compte supprimé");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => {
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get(`/transactions${filterAccount ? `?account_id=${filterAccount}` : ""}`).then((r) => setTxns(r.data));
  };
  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => { load(); }, [filterAccount]);

  const submitTxn = async () => {
    if (!txnForm.account_id || !txnForm.amount) return toast.error("Compte et montant requis");
    try {
      await api.post("/transactions", { ...txnForm, amount: Number(txnForm.amount) });
      toast.success("Mouvement enregistré");
      setOpenTxn(false);
      setTxnForm({ direction: "in", description: "", amount: "", account_id: "", category: "Autre", reference: "", date: todayISO() });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submitAcc = async () => {
    if (!accForm.name) return toast.error("Nom requis");
    try {
      await api.post("/accounts", { ...accForm, balance: Number(accForm.balance) || 0 });
      toast.success("Compte créé");
      setOpenAcc(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="treasury-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Caisse & Banque</h1>
          <p className="mt-1 text-sm text-slate-500">Soldes calculés automatiquement à chaque mouvement.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={openTransfer} onOpenChange={setOpenTransfer}>
            <DialogTrigger asChild><Button variant="outline" data-testid="transfer-button"><ArrowLeftRight className="mr-1 h-4 w-4" /> Transfert</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Transfert entre comptes</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Depuis</Label>
                  <Select value={trForm.from_account_id} onValueChange={(v) => setTrForm({ ...trForm, from_account_id: v })}>
                    <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Compte source…" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({fmtMoney(a.balance)})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Vers</Label>
                  <Select value={trForm.to_account_id} onValueChange={(v) => setTrForm({ ...trForm, to_account_id: v })}>
                    <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Compte destination…" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Montant (FCFA)</Label><Input type="number" value={trForm.amount} onChange={(e) => setTrForm({ ...trForm, amount: e.target.value })} data-testid="transfer-amount" /></div>
              </div>
              <DialogFooter>
                <Button data-testid="transfer-submit" onClick={async () => {
                  try {
                    await api.post("/accounts/transfer", { ...trForm, amount: Number(trForm.amount) });
                    toast.success("Transfert effectué");
                    setOpenTransfer(false);
                    setTrForm({ from_account_id: "", to_account_id: "", amount: "" });
                    load();
                  } catch (e) { toast.error(fmtErr(e)); }
                }}>Valider le transfert</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openAcc} onOpenChange={setOpenAcc}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-account-button"><Plus className="mr-1 h-4 w-4" /> Nouveau compte</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau compte</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nom</Label><Input value={accForm.name} onChange={(e) => setAccForm({ ...accForm, name: e.target.value })} data-testid="account-name-input" /></div>
                <div><Label>Type</Label>
                  <Select value={accForm.type} onValueChange={(v) => setAccForm({ ...accForm, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.account_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Solde initial (FCFA)</Label><Input type="number" value={accForm.balance} onChange={(e) => setAccForm({ ...accForm, balance: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submitAcc} data-testid="account-submit">Créer</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openTxn} onOpenChange={setOpenTxn}>
            <DialogTrigger asChild><Button data-testid="add-transaction-button"><Plus className="mr-1 h-4 w-4" /> Nouveau mouvement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau mouvement</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={txnForm.direction} onValueChange={(v) => setTxnForm({ ...txnForm, direction: v })}>
                    <SelectTrigger data-testid="txn-direction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in">Entrée (recette)</SelectItem>
                      <SelectItem value="out">Sortie (dépense)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Montant (FCFA)</Label><Input type="number" value={txnForm.amount} onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })} data-testid="txn-amount" /></div>
                <div className="col-span-2"><Label>Compte</Label>
                  <Select value={txnForm.account_id} onValueChange={(v) => setTxnForm({ ...txnForm, account_id: v })}>
                    <SelectTrigger data-testid="txn-account"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Description</Label><Input value={txnForm.description} onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })} data-testid="txn-description" /></div>
                <div><Label>Catégorie</Label><Input value={txnForm.category} onChange={(e) => setTxnForm({ ...txnForm, category: e.target.value })} placeholder="Salaires, Loyer, Publicité…" /></div>
                <div><Label>Référence</Label><Input value={txnForm.reference} onChange={(e) => setTxnForm({ ...txnForm, reference: e.target.value })} /></div>
                <div><Label>Date</Label><Input type="date" value={txnForm.date} onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={submitTxn} data-testid="txn-submit">Enregistrer</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {accounts.map((a) => (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`account-card-${a.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{a.name}</p>
              <Landmark className="h-4 w-4 text-slate-300" />
            </div>
            <p className={`font-mono-num mt-2 text-xl font-bold ${a.balance >= 0 ? "text-slate-900" : "text-red-600"}`}>{fmtMoney(a.balance)}</p>
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-slate-400">{a.type}</p>
              {isAdmin && (
                <button onClick={() => delAccount(a)} className="text-slate-300 hover:text-red-500" title="Supprimer le compte" data-testid={`delete-account-${a.name.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterAccount} onValueChange={(v) => setFilterAccount(v === "all" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="txn-filter-account"><SelectValue placeholder="Tous les comptes" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les comptes</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={() => downloadExcel("/export/excel?type=transactions")} data-testid="export-transactions-excel">Export Excel</Button>
      </div>

      <DataTable testidPrefix="transactions-table" rows={txns}
        searchKeys={["description", "reference", "category", "user_name"]}
        columns={[
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "direction", label: "Sens", render: (r) => r.direction === "in"
            ? <span className="flex items-center gap-1 text-emerald-600 font-semibold"><ArrowUpCircle className="h-4 w-4" /> Entrée</span>
            : <span className="flex items-center gap-1 text-red-600 font-semibold"><ArrowDownCircle className="h-4 w-4" /> Sortie</span> },
          { key: "description", label: "Description", render: (r) => <span className="text-slate-600">{r.description}</span> },
          { key: "category", label: "Catégorie", render: (r) => <span className="text-xs">{r.category}</span> },
          { key: "account_name", label: "Compte" },
          { key: "amount", label: "Montant", className: "text-right", render: (r) => (
            <span className={`font-mono-num font-semibold ${r.direction === "in" ? "text-emerald-600" : "text-red-600"}`}>
              {r.direction === "in" ? "+" : "−"}{fmtMoney(r.amount)}
            </span>
          ) },
          { key: "user_name", label: "Par", render: (r) => <span className="text-xs text-slate-500">{r.user_name}</span> },
          ...(isAdmin ? [{ key: "actions", label: "", sortable: false, render: (r) => (
            <button onClick={() => delTxn(r)} className="text-slate-300 hover:text-red-500" title="Supprimer le mouvement" data-testid={`delete-txn-${r.txn_number || r.id}`}><Trash2 className="h-4 w-4" /></button>
          ) }] : []),
        ]}
      />
    </div>
  );
}
